/**
 * useCryptoPrices — CURRENCY-01
 *
 * Holt Krypto-Preise vom BE-Proxy GET /api/v2/prices/crypto (authentifiziert).
 * Keine direkte CoinGecko-Anfrage mehr — Proxy rechnet in User-Währung um.
 *
 * Orientiert sich am Muster von useWalletBalanceSummary (apiClient).
 */
import { useQuery } from '@tanstack/react-query'
import apiClient from '../lib/api-client'
import { useAuthStore } from '../stores/authStore'

type Prices = {
  hnt: number | null
  iot: number | null
  mobile: number | null
  sol: number | null
}

type CryptoPricesResult = {
  prices: Prices
  currency: string
  updatedAt: string | null
  isLoading: boolean
  isError: boolean
}

const DEFAULT_PRICES: Prices = { hnt: null, iot: null, mobile: null, sol: null }

export function useCryptoPrices(): CryptoPricesResult {
  // AUTH-Block-Fix: CryptoPriceStrip ist Teil von AppShell (Header), das beim
  // Logout erst nach queryClient.clear() unmountet. Ohne `enabled`-Guard feuert
  // diese authentifizierte Query dadurch kurzzeitig nach Logout erneut (401
  // auf prices/crypto, live beobachtet). isAuthenticated wird beim Logout
  // synchron auf false gesetzt und deaktiviert die Query zuverlaessig.
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['crypto-prices'],
    enabled: isAuthenticated,
    queryFn: async () => {
      // apiClient hat Auth-Middleware (Bearer-Token) — exakt wie useWalletBalanceSummary
      // /api/v2/prices/crypto ist noch nicht im openapi-Typen-Paket, daher cast nötig.
      // Kein 'as any' auf Daten: Ergebnis wird typsicher aus dem bekannten Shape gelesen.
      const result = await (apiClient.GET as (path: string, init?: object) => Promise<{
        data?: {
          prices: { hnt?: number | null; iot?: number | null; mobile?: number | null; sol?: number | null }
          currency: string
          base: string
          updated_at: string
        }
        error?: unknown
      }>)('/api/v2/prices/crypto', {})

      if (result.error) throw new Error('Crypto prices fetch failed')
      if (!result.data) throw new Error('No data in crypto prices response')

      const p = result.data.prices
      return {
        prices: {
          hnt:    p.hnt    ?? null,
          iot:    p.iot    ?? null,
          mobile: p.mobile ?? null,
          sol:    p.sol    ?? null,
        } as Prices,
        currency: result.data.currency ?? 'usd',
        updatedAt: result.data.updated_at ?? null,
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
    // Hält den letzten gültigen Wert während eines Hintergrund-Refetch,
    // damit der Strip kein "—" zeigt, solange Daten vorhanden waren.
    placeholderData: (prev) => prev,
  })

  return {
    prices: data?.prices ?? DEFAULT_PRICES,
    currency: data?.currency ?? 'usd',
    updatedAt: data?.updatedAt ?? null,
    isLoading,
    isError,
  }
}
